<template>
  <div
    id="password-reset"
    class="d-flex align-items-center justify-content-center"
    style="min-height: 100vh; background-color: #e6e9ef;"
  >
    <!-- Error Alert - Using BAlert with model-value, don't use v-if -->
    <BAlert
      :model-value="!!errorMessage"
      variant="danger"
      dismissible
      class="error-alert-top"
      @update:model-value="(val) => { if (!val) errorMessage = ''; }"
    >
      {{ errorMessage }}
    </BAlert>

    <div class="password-reset-card">
      <!-- Reset Password Form Step -->
      <div v-if="currentStep === 'reset'">
        <div class="modal-title">
          Set New Password
        </div>
        <p class="text-center text-muted mb-4">
          Enter your new password below.
        </p>

        <!-- Password Requirements Guideline -->
        <div class="password-guidelines mb-4 p-3 bg-light rounded">
          <div class="font-weight-bold mb-2">
            Password Requirements:
          </div>
          <ul class="mb-0 small">
            <li>
              <span :class="passwordRequirementClass('minLength')">
                {{ passwordGuidelines.minLength }}
              </span>
            </li>
            <li v-if="passwordGuidelines.uppercase">
              <span :class="passwordRequirementClass('uppercase')">
                {{ passwordGuidelines.uppercase }}
              </span>
            </li>
            <li v-if="passwordGuidelines.lowercase">
              <span :class="passwordRequirementClass('lowercase')">
                {{ passwordGuidelines.lowercase }}
              </span>
            </li>
            <li v-if="passwordGuidelines.numbers">
              <span :class="passwordRequirementClass('numbers')">
                {{ passwordGuidelines.numbers }}
              </span>
            </li>
            <li v-if="passwordGuidelines.special">
              <span :class="passwordRequirementClass('special')">
                {{ passwordGuidelines.special }}
              </span>
            </li>
          </ul>
        </div>

        <BForm @submit.prevent="submitReset">
          <BFormGroup
            label="New Password"
            label-for="password"
            class="mb-3"
          >
            <BFormInput
              id="password"
              v-model="newPassword"
              type="password"
              placeholder="Enter new password"
              :state="passwordInputState"
              required
              @input="validatePasswordInput"
            />
            <small
              v-if="passwordErrors.length > 0"
              class="d-block text-danger mt-2"
            >
              <div
                v-for="error in passwordErrors"
                :key="error"
              >
                • {{ error }}
              </div>
            </small>
          </BFormGroup>

          <BFormGroup
            label="Confirm Password"
            label-for="confirm"
            class="mb-4"
          >
            <BFormInput
              id="confirm"
              v-model="confirmPassword"
              type="password"
              placeholder="Confirm your password"
              :state="confirmPasswordState"
              required
              @input="validatePasswordInput"
            />
            <small
              v-if="!passwordsMatch && confirmPassword"
              class="d-block text-danger mt-2"
            >
              • Passwords do not match
            </small>
          </BFormGroup>

          <BButton
            variant="primary"
            type="submit"
            class="w-100 mb-3"
            :disabled="!canSubmit"
          >
            {{ resetting ? "Resetting..." : "Reset Password" }}
          </BButton>
        </BForm>

        <div class="text-center">
          <router-link
            to="/Login"
            class="text-decoration-none"
          >
            Back to Login
          </router-link>
        </div>
      </div>

      <!-- Success Step -->
      <div
        v-else-if="currentStep === 'success'"
        class="text-center"
      >
        <div class="success-icon mb-3">
          ✓
        </div>
        <div class="modal-title text-success mb-3">
          Password Reset Successfully!
        </div>
        <p class="text-muted mb-4">
          Your password has been reset successfully. Redirecting to dashboard in 3 seconds...
        </p>
        <router-link
          to="/Dashboard"
          class="btn btn-success"
        >
          Go to Dashboard
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { BForm, BFormGroup, BFormInput, BButton, BAlert } from "bootstrap-vue-next";
import { useUserApi } from "@/composables/useUserApi";
import { useConstants, useCookie } from "@/composables/useAppGlobals";
import { usePasswordValidation } from "@/composables/usePasswordValidation";
import { handlePasswordResetError } from "@/utils/errorHandler";
import { useI18n } from "vue-i18n";

const userApi = useUserApi();
const router = useRouter();
const $const = useConstants();
const $cookie = useCookie();
const { t } = useI18n();

type StepType = "reset" | "success";

const currentStep = ref<StepType>("reset");
const newPassword = ref("");
const confirmPassword = ref("");
const resetting = ref(false);
const errorMessage = ref<string>("");

// Use password validation composable
const { passwordErrors, passwordGuidelines, validatePasswordField } = usePasswordValidation();

// Computed properties
const passwordsMatch = computed(() => newPassword.value === confirmPassword.value);
const passwordInputState = computed(() => {
  if (!newPassword.value) return null;
  return passwordErrors.value.length === 0 ? true : false;
});

const confirmPasswordState = computed(() => {
  if (!confirmPassword.value) return null;
  return passwordsMatch.value ? true : false;
});

const canSubmit = computed(
  () =>
    newPassword.value &&
    confirmPassword.value &&
    passwordsMatch.value &&
    passwordErrors.value.length === 0 &&
    !resetting.value
);

const validatePasswordInput = () => {
  validatePasswordField(newPassword.value);
};

const scheduleRedirect = () => {
  setTimeout(() => {
    router.push("/Dashboard");
  }, 3000);
};

const submitReset = async () => {
  errorMessage.value = "";

  // Final validation before submission
  if (!newPassword.value) {
    errorMessage.value = "Please enter a new password";
    return;
  }

  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = "Passwords do not match";
    return;
  }

  // Validate password strength
  const validation = validatePasswordField(newPassword.value);
  if (!validation) {
    return;
  }

  resetting.value = true;
  try {
    const response = await userApi.resetPassword(newPassword.value);
    
    // Verify is_reset is false
    if (response.is_reset === false) {
      // Update JWT token in cookies
      $cookie.setCookie(
        $const.COOKIES.KEY.USER_TOKEN,
        response.token,
        $const.COOKIES.CONFIG.EXPIRES_1M,
      );
      // Clear the is_reset flag
      $cookie.deleteCookie($const.COOKIES.KEY.USER_IS_RESET);
      currentStep.value = "success";
      scheduleRedirect();
    } else {
      errorMessage.value = t("errors.passwordResetFailed");
    }
  } catch (error: unknown) {
    errorMessage.value = handlePasswordResetError(error);
  } finally {
    resetting.value = false;
  }
};

// Helper function to determine requirement class
const passwordRequirementClass = (requirement: string) => {
  if (!newPassword.value) return "text-muted";
  
  const pwd = newPassword.value;
  let isMet = false;

  switch (requirement) {
    case "minLength":
      isMet = pwd.length >= 15;
      break;
    case "uppercase":
      isMet = /[A-Z]/.test(pwd);
      break;
    case "lowercase":
      isMet = /[a-z]/.test(pwd);
      break;
    case "numbers":
      isMet = /[0-9]/.test(pwd);
      break;
    case "special":
      isMet = /[!@#$%^&*\-_=+]/.test(pwd);
      break;
  }

  return isMet ? "text-success" : "text-muted";
};
</script>

<style scoped>
#password-reset {
  background-color: #e6e9ef;
  position: relative;
}

.error-alert-top {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.password-reset-card {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0px 4px 14px rgba(40, 75, 178, 0.1);
  padding: 40px 30px;
  max-width: 480px;
  width: 100%;
}

.modal-title {
  font-size: 28px;
  font-weight: bold;
  line-height: 40px;
  text-align: center;
  color: #212121;
  margin-bottom: 20px;
}

.success-icon {
  font-size: 60px;
  color: #28a745;
  text-align: center;
}

.text-success {
  color: #28a745 !important;
}

.password-guidelines {
  border-left: 4px solid #007bff;
}

.password-guidelines ul {
  list-style: none;
  padding-left: 0;
}

.password-guidelines li {
  margin-bottom: 0.5rem;
}

.text-success {
  color: #28a745 !important;
  font-weight: 500;
}

.text-danger {
  color: #dc3545 !important;
}

.text-muted {
  color: #6c757d;
}

.font-weight-bold {
  font-weight: 600;
}
</style>
